/**
 * @file: ReportCardRenderer.tsx
 *
 * An inspection report card (`report_card` payload). Full form is the
 * thumbnail + title + status/plate/badge + meta-rows card built from
 * `describeChatCard`; `quoted` is a compact "icon + title" chip.
 */

import { StatusBadgeV2 } from '@euler/app/components/task-card/components/StatusBadgeV2';
import { describeChatCard } from '@euler/app/flows/chat/utils/chatCardDescriptor';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatMessagePayloadReportCard } from '@euler/model/chat';
import { FileText } from 'lucide-react-native';
import { FC, memo, useCallback } from 'react';
import { View } from 'react-native';
import {
  CardFooter,
  CardMeta,
  CardQuoteChip,
  CardSurface,
  CardThumbnail,
  CardTitle,
  Pill,
} from './cardPrimitives';
import { PayloadRendererProps } from './types';

interface Props extends PayloadRendererProps<ChatMessagePayloadReportCard> {
  onOpenCard?: (payload: ChatMessagePayloadReportCard) => void;
}

export const ReportCardRenderer: FC<Props> = memo(
  ({ payload, quoted, onOpenCard }) => {
    const { rpx } = useResponsiveContext();
    const view = describeChatCard(payload);

    const handlePress = useCallback(
      () => onOpenCard?.(payload),
      [onOpenCard, payload],
    );

    if (quoted) {
      return (
        <CardQuoteChip
          icon={<FileText color="#3b82f6" size={rpx(15)} />}
          label={view.title}
        />
      );
    }

    return (
      <CardSurface onPress={handlePress} accent={view.statusTone}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <CardThumbnail view={view} />
          <View style={{ flex: 1, marginLeft: rpx(10) }}>
            <CardTitle text={view.title} />
            {view.statusLabel || view.plate || view.badge ? (
              <View
                style={{
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  marginTop: rpx(5),
                }}
              >
                {view.statusTone && view.statusLabel ? (
                  <StatusBadgeV2
                    label={view.statusLabel}
                    palette={view.statusTone}
                  />
                ) : null}
                {view.plate ? <Pill text={view.plate} tone="plate" /> : null}
                {view.badge ? <Pill text={view.badge} tone="accent" /> : null}
              </View>
            ) : null}
            {view.metaRows.map((m, i) => (
              <CardMeta key={i} text={`${m.label}: ${m.value}`} />
            ))}
          </View>
        </View>
        <CardFooter label={view.footerLabel} />
      </CardSurface>
    );
  },
);
